import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import PublicDealerSite from '../pages/dealers/PublicDealerSite';
import PublicCarDetail from '../pages/dealers/PublicCarDetail';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Hostnames that should always serve the normal Propitstop app —
// preview URLs, localhost, and the platform's own custom domain.
// Anything else is treated as a possible dealer domain and probed.
const PLATFORM_HOST_SUFFIXES = [
  'preview.emergentagent.com',
  'emergent.host',
  'localhost',
  '127.0.0.1',
  'propitstop.in',
];

const isPlatformHost = (host) => {
  const h = (host || '').toLowerCase();
  return PLATFORM_HOST_SUFFIXES.some((suf) => h === suf || h.endsWith(`.${suf}`) || h.endsWith(suf));
};

/**
 * CustomDomainGate — runs once at boot. Reads `window.location.hostname`,
 * probes `/api/public/dealers/resolve-domain/<host>`, and if a dealer
 * claims the host, renders THAT dealer's microsite at path `/` and
 * `/cars/:listingId`. If not (or on the platform's own host), passes
 * through to the normal app routes.
 *
 * The dealer slug is stashed on `window.__DEALER_HOST_SLUG__` so the
 * existing microsite pages (which read slug from useParams) can pick it
 * up when the URL has no `:slug` segment.
 */
export default function CustomDomainGate({ children }) {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const platform = isPlatformHost(host);
  const [state, setState] = useState({ loading: !platform, slug: null });

  useEffect(() => {
    if (platform) return undefined;
    let cancelled = false;
    axios
      .get(`${API_BASE}/api/public/dealers/resolve-domain/${encodeURIComponent(host)}`)
      .then((res) => {
        if (cancelled) return;
        const slug = res.data?.slug || null;
        if (slug) {
          window.__DEALER_HOST_SLUG__ = slug;
        }
        setState({ loading: false, slug });
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, slug: null });
      });
    return () => { cancelled = true; };
  }, [host, platform]);

  // On the platform's own host — no probing needed, render as usual.
  if (platform) return children;

  // Still probing on a non-platform host — brief blank frame instead of a
  // flash-of-home-page.
  if (state.loading) {
    return <div className="min-h-screen bg-slate-950" data-testid="custom-domain-loading" />;
  }

  // No dealer claims this domain → fall back to normal app routes so the
  // Propitstop home page (or a 404 inside it) renders.
  if (!state.slug) return children;

  // Dealer resolved — swap to a scoped route set that keeps the URL clean.
  return (
    <Routes>
      <Route path="/" element={<PublicDealerSite />} />
      <Route path="/cars/:listingId" element={<PublicCarDetail />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
